import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Flame, Play } from 'lucide-react';
import { collection, query, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Link } from 'react-router-dom';

interface Oyun {
  id: string;
  name: string;
  imageUrl: string;
  provider: string;
  rating: number;
  playCount: number;
  order?: number;
  isActive?: boolean;
  isPopular?: boolean;
}

const PopulerOyunlar: React.FC = () => {
  const [oyunlar, setOyunlar] = useState<Oyun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Tüm oyunları çek, sonra client tarafında filtrele
    const oyunlarQuery = query(
      collection(db, 'populerOyunlar'),
      limit(50)
    );

    const unsubscribe = onSnapshot(oyunlarQuery, (snapshot) => {
      const allData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Oyun[];

      // Client tarafında filtreleme ve sıralama
      const filteredData = allData
        .filter(oyun => oyun.isActive !== false && oyun.isPopular !== false)
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .slice(0, 8);

      setOyunlar(filteredData);
      setLoading(false);
    }, () => {
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Demo veriler
  const demoOyunlar = [
    { id: '1', name: 'Sweet Bonanza', imageUrl: 'https://via.placeholder.com/200x150/6366f1/ffffff?text=Sweet+Bonanza', provider: 'Pragmatic Play', rating: 4.8, playCount: 125000 },
    { id: '2', name: 'Gates of Olympus', imageUrl: 'https://via.placeholder.com/200x150/8b5cf6/ffffff?text=Gates+of+Olympus', provider: 'Pragmatic Play', rating: 4.9, playCount: 98000 },
    { id: '3', name: 'Big Bass Bonanza', imageUrl: 'https://via.placeholder.com/200x150/ec4899/ffffff?text=Big+Bass', provider: 'Pragmatic Play', rating: 4.7, playCount: 87000 },
    { id: '4', name: 'Starlight Princess', imageUrl: 'https://via.placeholder.com/200x150/f59e0b/ffffff?text=Starlight', provider: 'Pragmatic Play', rating: 4.6, playCount: 76000 },
    { id: '5', name: 'Sugar Rush', imageUrl: 'https://via.placeholder.com/200x150/10b981/ffffff?text=Sugar+Rush', provider: 'Pragmatic Play', rating: 4.5, playCount: 65000 },
    { id: '6', name: 'The Dog House', imageUrl: 'https://via.placeholder.com/200x150/3b82f6/ffffff?text=Dog+House', provider: 'Pragmatic Play', rating: 4.4, playCount: 54000 },
    { id: '7', name: 'Fruit Party', imageUrl: 'https://via.placeholder.com/200x150/ef4444/ffffff?text=Fruit+Party', provider: 'Pragmatic Play', rating: 4.3, playCount: 43000 },
    { id: '8', name: 'Madame Destiny', imageUrl: 'https://via.placeholder.com/200x150/a855f7/ffffff?text=Madame+Destiny', provider: 'Pragmatic Play', rating: 4.2, playCount: 32000 },
  ];

  const displayData = oyunlar.length > 0 ? oyunlar : demoOyunlar;

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
            <Flame className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">En Popüler Oyunlar</h2>
        </div>
        <Link to="/slotlar/populer" className="text-primary-400 hover:text-primary-300 text-sm">
          Tümünü Gör →
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 flex-1">
        {loading ? (
          <div className="col-span-4 animate-pulse">
            <div className="grid grid-cols-4 gap-1">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-dark-700/50 rounded-lg" />
              ))}
            </div>
          </div>
        ) : (
          displayData.map((oyun, index) => (
            <motion.div
              key={oyun.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="group relative rounded-xl overflow-hidden cursor-pointer"
            >
              <div className="aspect-square bg-transparent relative overflow-hidden rounded-xl">
                <img
                  src={oyun.imageUrl}
                  alt={oyun.name}
                  className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300 origin-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center">
                    <Play className="w-6 h-6 text-white ml-1" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default PopulerOyunlar;
